import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import {
  GET_PRODUCTION_RECIPE,
  GET_INVENTORY_INGREDIENTS,
  CREATE_PRODUCTION_RECIPE,
  UPDATE_PRODUCTION_RECIPE,
  DELETE_PRODUCTION_RECIPE,
} from "./queries";
import { UNIT_OPTIONS, InventoryIngredient } from "./models";
import { DeleteModal } from "../../modals/deletes";

type Params = { id?: string };

interface InputRow {
  key: number;
  ingredient_id: string;
  ingredientName: string;
  quantity: string;
  unit: string;
  cost: number;
}

const emptyRow = (key: number): InputRow => ({
  key,
  ingredient_id: "",
  ingredientName: "",
  quantity: "",
  unit: "g",
  cost: 0,
});

const ProductionRecipeForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const isEdit = !!id;

  const [name, setName] = useState("");
  const [outputIngredientId, setOutputIngredientId] = useState("");
  const [outputIngredientName, setOutputIngredientName] = useState("");
  const [outputQuantity, setOutputQuantity] = useState("");
  const [outputUnit, setOutputUnit] = useState("kg");
  const [notes, setNotes] = useState("");
  const [inputs, setInputs] = useState<InputRow[]>([emptyRow(0)]);
  const [rowKey, setRowKey] = useState(1);
  const [showDelete, setShowDelete] = useState(false);

  // Ingredient search
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [activeSearchRow, setActiveSearchRow] = useState<number | null>(null);
  const [outputSearch, setOutputSearch] = useState(false);

  const [fetchRecipe, { loading: loadingRecipe }] = useLazyQuery(
    GET_PRODUCTION_RECIPE,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        const r = data.getProductionRecipe;
        if (!r) return;
        setName(r.name);
        setOutputIngredientId(String(r.output_ingredient_id));
        setOutputIngredientName(r.output_ingredient?.name || "");
        setOutputQuantity(String(r.output_quantity));
        setOutputUnit(r.output_unit);
        setNotes(r.notes || "");
        if (r.inputs && r.inputs.length > 0) {
          setInputs(
            r.inputs.map((inp: any, i: number) => ({
              key: i,
              ingredient_id: String(inp.ingredient_id),
              ingredientName: inp.ingredient?.name || "",
              quantity: String(inp.quantity),
              unit: inp.unit,
              cost: inp.ingredient?.cost ? Number(inp.ingredient.cost) : 0,
            }))
          );
          setRowKey(r.inputs.length);
        }
      },
    }
  );

  const [searchIngredients, { data: ingredientData }] = useLazyQuery(
    GET_INVENTORY_INGREDIENTS,
    { fetchPolicy: "no-cache" }
  );

  const [createRecipe, { loading: creating }] = useMutation(
    CREATE_PRODUCTION_RECIPE,
    {
      onCompleted: () => {
        toast.success(lang("Receta creada", "Recipe created"));
        history.push("/inventory/production/recipes");
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const [updateRecipe, { loading: updating }] = useMutation(
    UPDATE_PRODUCTION_RECIPE,
    {
      onCompleted: () => {
        toast.success(lang("Receta actualizada", "Recipe updated"));
        history.push("/inventory/production/recipes");
      },
      onError: (err) => toast.error(err.message),
    }
  );

  const [deleteRecipe] = useMutation(
    DELETE_PRODUCTION_RECIPE,
    {
      onCompleted: () => {
        toast.success(lang("Receta eliminada", "Recipe deleted"));
        history.push("/inventory/production/recipes");
      },
      onError: (err) => toast.error(err.message),
    }
  );

  useEffect(() => {
    if (isEdit) fetchRecipe({ variables: { id } });
    //eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (ingredientSearch.length >= 2) {
      searchIngredients({ variables: { search: ingredientSearch, take: 10 } });
    }
    //eslint-disable-next-line
  }, [ingredientSearch]);

  const selectIngredient = (ing: InventoryIngredient) => {
    if (outputSearch) {
      setOutputIngredientId(ing.id);
      setOutputIngredientName(ing.name);
      setOutputUnit(ing.unit);
      setOutputSearch(false);
    } else if (activeSearchRow !== null) {
      setInputs((prev) =>
        prev.map((row) =>
          row.key === activeSearchRow
            ? {
                ...row,
                ingredient_id: ing.id,
                ingredientName: ing.name,
                unit: ing.unit,
                cost: ing.cost ? Number(ing.cost) : 0,
              }
            : row
        )
      );
      setActiveSearchRow(null);
    }
    setIngredientSearch("");
  };

  const addRow = () => {
    setInputs((prev) => [...prev, emptyRow(rowKey)]);
    setRowKey((k) => k + 1);
  };

  const removeRow = (key: number) => {
    setInputs((prev) => prev.filter((r) => r.key !== key));
  };

  const updateRow = (key: number, field: string, value: string) => {
    setInputs((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validInputs = inputs.filter((r) => r.ingredient_id && r.quantity);
    if (!name || !outputIngredientId || !outputQuantity) {
      toast.error(
        lang("Completa los campos requeridos", "Complete required fields")
      );
      return;
    }
    if (validInputs.length === 0) {
      toast.error(
        lang(
          "Agrega al menos un insumo",
          "Add at least one input ingredient"
        )
      );
      return;
    }

    const inputsData = validInputs.map((r) => ({
      ingredient_id: parseInt(r.ingredient_id),
      quantity: parseFloat(r.quantity),
      unit: r.unit,
    }));

    if (isEdit) {
      updateRecipe({
        variables: {
          id,
          name,
          output_quantity: parseFloat(outputQuantity),
          output_unit: outputUnit,
          notes: notes || null,
          inputs: inputsData,
        },
      });
    } else {
      createRecipe({
        variables: {
          name,
          output_ingredient_id: parseInt(outputIngredientId),
          output_quantity: parseFloat(outputQuantity),
          output_unit: outputUnit,
          notes: notes || null,
          inputs: inputsData,
        },
      });
    }
  };

  const saving = creating || updating;

  if (loadingRecipe) return <LoaderDots fullSize />;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {isEdit
            ? lang("Editar Receta de Producción", "Edit Production Recipe")
            : lang("Nueva Receta de Producción", "New Production Recipe")}
        </h4>
        <button
          className="btn btn-outline-secondary"
          onClick={() => history.push("/inventory/production/recipes")}
        >
          {lang("Volver", "Back")}
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="material_card p-3 mb-3">
          <div className="row">
            <div className="col-lg-6">
              <div className="form-group">
                <label>{lang("Nombre", "Name")} *</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={lang("Ej: Salsa Roja", "E.g: Red Sauce")}
                  required
                />
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <div className="form-group position-relative">
                <label>
                  {lang("Ingrediente Resultado", "Output Ingredient")} *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={
                    outputSearch
                      ? ingredientSearch
                      : outputIngredientName || ""
                  }
                  onChange={(e) => {
                    setOutputSearch(true);
                    setActiveSearchRow(null);
                    setIngredientSearch(e.target.value);
                  }}
                  onFocus={() => {
                    setOutputSearch(true);
                    setActiveSearchRow(null);
                  }}
                  placeholder={lang(
                    "Buscar ingrediente...",
                    "Search ingredient..."
                  )}
                />
                {outputSearch &&
                  ingredientData?.getInventoryIngredients?.length > 0 && (
                    <div
                      className="list-group position-absolute w-100"
                      style={{ zIndex: 1000, maxHeight: 200, overflowY: "auto" }}
                    >
                      {ingredientData.getInventoryIngredients.map(
                        (ing: InventoryIngredient) => (
                          <button
                            key={ing.id}
                            type="button"
                            className="list-group-item list-group-item-action py-1"
                            onClick={() => selectIngredient(ing)}
                          >
                            {ing.name}{" "}
                            <small className="text-muted">({ing.unit})</small>
                          </button>
                        )
                      )}
                    </div>
                  )}
              </div>
            </div>
            <div className="col-lg-3">
              <div className="form-group">
                <label>{lang("Cantidad Esperada", "Expected Yield")} *</label>
                <input
                  type="number"
                  step="0.0001"
                  className="form-control"
                  value={outputQuantity}
                  onChange={(e) => setOutputQuantity(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="col-lg-3">
              <div className="form-group">
                <label>{lang("Unidad", "Unit")} *</label>
                <select
                  className="custom-select"
                  value={outputUnit}
                  onChange={(e) => setOutputUnit(e.target.value)}
                >
                  {UNIT_OPTIONS.map((u) => (
                    <option key={u.value} value={u.value}>
                      {u.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-6">
              <div className="form-group">
                <label>{lang("Notas", "Notes")}</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Input ingredients */}
        <div className="material_card p-3 mb-3">
          <h5>{lang("Insumos", "Input Ingredients")}</h5>
          <table className="table table-sm vertical_middle">
            <thead className="thead-light">
              <tr>
                <th style={{ width: "40%" }}>
                  {lang("Ingrediente", "Ingredient")}
                </th>
                <th style={{ width: "20%" }}>
                  {lang("Cantidad", "Quantity")}
                </th>
                <th style={{ width: "20%" }}>{lang("Unidad", "Unit")}</th>
                <th style={{ width: "10%" }}>{lang("Costo", "Cost")}</th>
                <th style={{ width: "10%" }}></th>
              </tr>
            </thead>
            <tbody>
              {inputs.map((row) => (
                <tr key={row.key}>
                  <td className="position-relative">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={
                        activeSearchRow === row.key
                          ? ingredientSearch
                          : row.ingredientName
                      }
                      onChange={(e) => {
                        setActiveSearchRow(row.key);
                        setOutputSearch(false);
                        setIngredientSearch(e.target.value);
                      }}
                      onFocus={() => {
                        setActiveSearchRow(row.key);
                        setOutputSearch(false);
                      }}
                      placeholder={lang("Buscar...", "Search...")}
                    />
                    {activeSearchRow === row.key &&
                      ingredientData?.getInventoryIngredients?.length > 0 && (
                        <div
                          className="list-group position-absolute w-100"
                          style={{
                            zIndex: 1000,
                            maxHeight: 200,
                            overflowY: "auto",
                          }}
                        >
                          {ingredientData.getInventoryIngredients.map(
                            (ing: InventoryIngredient) => (
                              <button
                                key={ing.id}
                                type="button"
                                className="list-group-item list-group-item-action py-1"
                                onClick={() => selectIngredient(ing)}
                              >
                                {ing.name}{" "}
                                <small className="text-muted">
                                  ({ing.unit})
                                </small>
                              </button>
                            )
                          )}
                        </div>
                      )}
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.0001"
                      className="form-control form-control-sm"
                      value={row.quantity}
                      onChange={(e) =>
                        updateRow(row.key, "quantity", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <select
                      className="custom-select custom-select-sm"
                      value={row.unit}
                      onChange={(e) =>
                        updateRow(row.key, "unit", e.target.value)
                      }
                    >
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="text-muted">
                    {row.cost && row.quantity
                      ? `$${(row.cost * parseFloat(row.quantity || "0")).toFixed(
                          2
                        )}`
                      : "—"}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeRow(row.key)}
                      disabled={inputs.length <= 1}
                    >
                      <i className="fa fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={addRow}
          >
            <i className="fa fa-plus mr-1" />
            {lang("Agregar Insumo", "Add Input")}
          </button>
        </div>

        <div className="d-flex">
          <button
            type="submit"
            className="btn btn-primary mr-2"
            disabled={saving}
          >
            {saving ? (
              <i className="fa fa-spinner fa-spin" />
            ) : (
              lang("Guardar", "Save")
            )}
          </button>
          {isEdit && (
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => setShowDelete(true)}
            >
              {lang("Eliminar", "Delete")}
            </button>
          )}
        </div>
      </form>

      <DeleteModal
        open={showDelete}
        title={lang("Eliminar Receta", "Delete Recipe")}
        toggle={() => setShowDelete(false)}
        handleConfirm={() => deleteRecipe({ variables: { id } })}
        confirm_txt={lang("Eliminar", "Delete")}
      >
        <p>
          {lang(
            "¿Estás seguro de eliminar esta receta de producción?",
            "Are you sure you want to delete this production recipe?"
          )}
        </p>
      </DeleteModal>
    </>
  );
};

export default ProductionRecipeForm;
